import {
    ConfigurationData,
    NetworkSetupData,
    MappingValue,
    ValidationError,
    ValidationResult,
} from './types';
import { MAPPING_FIELDS } from './configurationConstants';

const HOSTNAME_OR_IP_REGEX = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)*[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$|^(?:\d{1,3}\.){3}\d{1,3}$/;

const USERNAME_REGEX = /^[a-zA-Z0-9_-]+$/;

const FILE_PATH_REGEX = /^(?:\/|\.\/|\.\.\/)?(?:[a-zA-Z0-9_.-]+\/?)+$/;

export const isValidSftpUrl = (url: string): boolean => {
    const trimmedUrl = url.trim();
    return HOSTNAME_OR_IP_REGEX.test(trimmedUrl);
};

export const isValidUsername = (username: string): boolean => {
    return USERNAME_REGEX.test(username.trim());
};

export const isValidPort = (port: unknown): { valid: boolean; error?: string } => {
    if (port === null || port === undefined || port === '') {
        return { valid: false, error: 'Port is required' };
    }

    const portNum = typeof port === 'string' ? parseInt(port, 10) : port;

    if (typeof portNum !== 'number' || isNaN(portNum)) {
        return { valid: false, error: 'Port must be a numeric value' };
    }

    if (!Number.isInteger(portNum)) {
        return { valid: false, error: 'Port must be a whole number' };
    }

    if (portNum < 1 || portNum > 65535) {
        return { valid: false, error: 'Port must be between 1 and 65535' };
    }

    return { valid: true };
};

export const isValidFilePath = (path: string): boolean => {
    const trimmedPath = path.trim();
    return FILE_PATH_REGEX.test(trimmedPath);
};

const formatFieldLabel = (fieldKey: string): string => {
    return fieldKey
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export const validateNetworkSetup = (networkSetup: NetworkSetupData | undefined): ValidationError[] => {
    const errors: ValidationError[] = [];
    const tab = 'Network Setup';

    if (!networkSetup) {
        errors.push({ field: 'network_setup', message: 'Network setup is required', tab });
        return errors;
    }

    if (!networkSetup.import_type?.trim()) {
        errors.push({ field: 'import_type', message: 'Import Type is required', tab });
    }

    if (!networkSetup.sftp_host?.trim()) {
        errors.push({ field: 'sftp_host', message: 'SFTP URL is required', tab });
    } else if (!isValidSftpUrl(networkSetup.sftp_host)) {
        errors.push({
            field: 'sftp_host',
            message: 'SFTP URL must be a valid hostname or IP address',
            tab,
        });
    }

    if (!networkSetup.sftp_username?.trim()) {
        errors.push({ field: 'sftp_username', message: 'Username is required', tab });
    } else if (!isValidUsername(networkSetup.sftp_username)) {
        errors.push({
            field: 'sftp_username',
            message: 'Username must contain only alphanumeric characters, underscores, or hyphens',
            tab,
        });
    }

    if (!networkSetup.sftp_password?.trim()) {
        errors.push({ field: 'sftp_password', message: 'Password is required', tab });
    }

    const portValidation = isValidPort(networkSetup.port);
    if (!portValidation.valid) {
        errors.push({ field: 'port', message: portValidation.error!, tab });
    }

    if (!networkSetup.file_location?.trim()) {
        errors.push({ field: 'file_location', message: 'File location is required', tab });
    } else if (!isValidFilePath(networkSetup.file_location)) {
        errors.push({
            field: 'file_location',
            message: 'File location must be a valid path (e.g., /path/to/folder/ or uploads/)',
            tab,
        });
    }

    return errors;
};

export const validateProductMappings = (
    product: { [key: string]: MappingValue } | undefined
): ValidationError[] => {
    const errors: ValidationError[] = [];
    const tab = 'Products';

    if (!product) {
        errors.push({ field: 'product', message: 'Product mappings are required', tab });
        return errors;
    }

    const requiredFields = MAPPING_FIELDS.filter((field) => field.label !== 'upc');

    requiredFields.forEach((fieldConfig) => {
        const mapping = product[fieldConfig.key];

        if (!mapping) {
            errors.push({
                field: fieldConfig.key,
                message: `${formatFieldLabel(fieldConfig.key)} mapping is required`,
                tab,
            });
            return;
        }

        if (!mapping.source?.trim()) {
            errors.push({
                field: `${fieldConfig.key}.source`,
                message: `${formatFieldLabel(fieldConfig.key)} source is required`,
                tab,
            });
        }

        if (!mapping.value?.trim()) {
            errors.push({
                field: `${fieldConfig.key}.value`,
                message: `${formatFieldLabel(fieldConfig.key)} value is required`,
                tab,
            });
        }
    });

    return errors;
};

export const validateAvailability = (
    availability: { availability: string | null } | undefined
): ValidationError[] => {
    const errors: ValidationError[] = [];
    const tab = 'Availability';

    if (!availability) {
        errors.push({ field: 'availability', message: 'Availability data is required', tab });
        return errors;
    }

    if (!availability.availability?.trim()) {
        errors.push({ field: 'availability', message: 'Availability format is required', tab });
    }

    return errors;
};

export const validateConfiguration = (config: ConfigurationData | null): ValidationResult => {
    if (!config) {
        return {
            isValid: false,
            errors: [{ field: 'config', message: 'Configuration data is missing', tab: 'Network Setup' }],
        };
    }

    const networkErrors = validateNetworkSetup(config.network_setup);
    const productErrors = validateProductMappings(config.product);
    const availabilityErrors = validateAvailability(config.availability);

    const allErrors = [...networkErrors, ...productErrors, ...availabilityErrors];

    return {
        isValid: allErrors.length === 0,
        errors: allErrors,
    };
};

export const getErrorsByTab = (
    errors: ValidationError[]
): Record<'Network Setup' | 'Products' | 'Availability', ValidationError[]> => {
    return {
        'Network Setup': errors.filter((e) => e.tab === 'Network Setup'),
        'Products': errors.filter((e) => e.tab === 'Products'),
        'Availability': errors.filter((e) => e.tab === 'Availability'),
    };
};

export const getFieldError = (errors: ValidationError[], fieldKey: string): string | null => {
    const error = errors.find(
        (e) => e.field === fieldKey || e.field.startsWith(`${fieldKey}.`)
    );
    return error?.message ?? null;
};